/**
 * Rank bands for App Store search positions, and why the colour is spent where
 * it is.
 *
 * Measured against the live kw_ranks table on 2026-08-18: 1,709 stored
 * positions over 1,515 apps, min #1, median #86, max #188. By band:
 * top 3 1.8%, 4-10 4.1%, 11-25 8.8%, 26-50 14.6%, past 50 70.8%.
 *
 * So a top-3 position is genuinely rare and is the only band that gets a
 * filled chip. Seven rows in ten fall past #50 and stay neutral, which is what
 * keeps the column a list instead of a heat map. The number is always printed
 * inside the chip, so the tier is a second encoding of something already
 * readable and nothing here is carried by colour alone.
 *
 * Terminal mode gets the same five steps as an emerald intensity ramp rather
 * than a hue change, because every other surface in that theme is one colour.
 *
 * Its own module rather than a helper inside the panel because the boundaries
 * are the product decision here and the panel cannot be rendered under test:
 * this repository's vitest runs in the node environment with no jsdom and no
 * testing-library, so a band that drifts is only catchable as pure logic.
 */
export type RankTier = { label: string; chip: string; terminalChip: string };

export function rankTier(rank: number): RankTier {
  if (rank <= 3) {
    return {
      label: "Top 3",
      chip: "bg-emerald-700 text-white dark:bg-emerald-500 dark:text-emerald-950",
      terminalChip: "bg-emerald-400 text-black",
    };
  }
  if (rank <= 10) {
    return {
      label: "Top 10",
      chip:
        "bg-emerald-500/10 text-emerald-700 ring-1 ring-emerald-500/25 " +
        "dark:text-emerald-300 dark:ring-emerald-500/35",
      terminalChip: "bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-500/50",
    };
  }
  if (rank <= 25) {
    return {
      label: "Top 25",
      chip:
        "bg-amber-500/10 text-amber-700 ring-1 ring-amber-500/25 " +
        "dark:text-amber-300 dark:ring-amber-500/35",
      terminalChip: "bg-emerald-500/10 text-emerald-400 ring-1 ring-emerald-500/30",
    };
  }
  if (rank <= 50) {
    return {
      label: "Top 50",
      chip: "bg-muted/60 text-secondary-foreground ring-1 ring-border",
      terminalChip: "text-emerald-400/80 ring-1 ring-emerald-700/60",
    };
  }
  return {
    label: "Past 50",
    chip: "text-secondary-foreground ring-1 ring-border/70",
    terminalChip: "text-emerald-500/60 ring-1 ring-emerald-800/60",
  };
}
